import * as React from 'react';
import { PanelLeftOpen, PanelLeftClose } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

/** A single history instance: inline on desktop, an accessible picker elsewhere. */
export function useAgentDesktopHistory(compact: boolean) {
  const [wide, setWide] = React.useState(() => typeof window.matchMedia === 'function' ? window.matchMedia('(min-width: 1024px)').matches : true);
  React.useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia('(min-width: 1024px)');
    const update = () => setWide(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  return !compact && wide;
}

interface AgentThreadHistoryProps {
  desktop: boolean;
  inlineMobile?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  triggerRef?: React.RefObject<HTMLButtonElement | null>;
  title?: string;
  description?: string;
}
export function AgentThreadHistory({ desktop, inlineMobile = false, open, onOpenChange, children, triggerRef, title = 'Your conversations', description = 'Private to you in this scope. Other people can’t see them.' }: AgentThreadHistoryProps) {
  if (desktop) return <aside aria-label="Chats rail" className={open ? "flex min-h-0 w-[min(22rem,100vw)] max-w-[22rem] shrink-0 flex-col overflow-hidden border-r bg-background" : "flex min-h-0 w-10 shrink-0 flex-col items-center overflow-hidden border-r bg-background"}>
    <div className="flex w-full shrink-0 items-center justify-between gap-1 border-b p-1">
      {open && <span className="px-1 text-sm font-semibold">History</span>}
      <Button type="button" size="icon" variant="ghost" className="size-8" aria-label={open ? 'Collapse chats' : 'Expand chats'} aria-expanded={open} onClick={() => onOpenChange(!open)}>{open ? <PanelLeftClose className="size-4" /> : <PanelLeftOpen className="size-4" />}</Button>
    </div>
    {!open && <span className="mt-2 rotate-180 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground [writing-mode:vertical-rl]">History</span>}
    <div hidden={!open} className="min-h-0 min-w-0 flex-1 overflow-hidden">{children}</div>
  </aside>;
  if (inlineMobile) return <>{children}</>;
  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="flex flex-col gap-3" onCloseAutoFocus={event => { if (triggerRef?.current) { event.preventDefault(); triggerRef.current.focus(); } }}>
      <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>{description}</DialogDescription></DialogHeader>
      {children}
    </DialogContent>
  </Dialog>;
}
